import type { LiveStatus, LiveTrip, LiveVehicle, TripProgress, VehicleActiveTrip, VehiclePositionEvent, VehicleStateEvent } from '@rfleet/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import type { Map as MlMap } from 'maplibre-gl';
import { ChevronDown, Crosshair, Eye, EyeOff, History, Key, Phone, Plus, Radio, Route, Smartphone, X, ZoomIn } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Skeleton } from '../../components/ui';
import { api } from '../../lib/api';
import { fmtAgo, fmtDuration, fmtNumber } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useSocketEvent } from '../../lib/socket';
import { LiveTripsLayer, tripColor, type LiveTripPath } from '../../map/LiveTripsLayer';
import { MapView } from '../../map/MapView';
import { MarkersLayer, type PointMarker } from '../../map/MarkersLayer';
import { PlacesLayer, type PlaceGeom } from '../../map/PlacesLayer';
import { RouteLayer, type RoutePoint } from '../../map/RouteLayer';
import { VEHICLES_LAYER_ID, VehiclesLayer, vehicleKind, type VehicleMarker } from '../../map/VehiclesLayer';
import { TripProgressCard } from './TripProgressCard';
import type { Group } from '../vehicles/VehiclesPage';

const TONE: Record<LiveStatus, 'success' | 'neutral' | 'warning' | 'danger' | 'info'> = { MOVING: 'success', STOPPED: 'neutral', IDLE: 'warning', OFFLINE: 'neutral', UNKNOWN: 'neutral' };
const BAR: Record<LiveStatus, string> = { MOVING: 'bg-success', STOPPED: 'bg-neutral', IDLE: 'bg-warning', OFFLINE: 'bg-neutral', UNKNOWN: 'bg-neutral' };

export function LivePage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(params.get('vehicle'));
  const [follow, setFollow] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LiveStatus | ''>('');
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const mapRef = useRef<MlMap | null>(null);
  const qc = useQueryClient();
  const can = useSession((s) => s.can);

  const snapshot = useQuery({ queryKey: ['live-vehicles'], queryFn: () => api<LiveVehicle[]>('/live/vehicles'), refetchInterval: 60_000 });
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: () => api<Group[]>('/vehicle-groups') });
  const places = useQuery({ queryKey: ['places-all'], queryFn: () => api<PlaceGeom[]>('/places/all') });

  // Local live state overlaid on the snapshot
  const [overrides, setOverrides] = useState<Record<string, Partial<LiveVehicle['live']>>>({});
  useSocketEvent(
    'vehicle.position',
    useCallback((e: VehiclePositionEvent) => setOverrides((o) => ({ ...o, [e.vehicleId]: { ...o[e.vehicleId], lat: e.lat, lon: e.lon, speedKph: e.speedKph, heading: e.heading, lastAt: e.at, activeSource: e.source } })), []),
  );
  useSocketEvent(
    'vehicle.state',
    useCallback((e: VehicleStateEvent) => setOverrides((o) => ({ ...o, [e.vehicleId]: { ...o[e.vehicleId], status: e.status, statusSince: e.statusSince, ignition: e.ignition, odometerKm: e.odometerKm, fuelLevelPct: e.fuelLevelPct, placeId: e.placeId ?? null, tripId: e.tripId ?? null } })), []),
  );
  useEffect(() => setOverrides({}), [snapshot.dataUpdatedAt]);

  const vehicles = useMemo(() => (snapshot.data ?? []).map((v) => ({ ...v, live: { ...v.live, ...overrides[v.id] } })), [snapshot.data, overrides]);
  const visible = useMemo(
    () => vehicles.filter((v) => !hiddenGroups.has(v.groupId ?? '__none') && (!statusFilter || v.live.status === statusFilter) && (!search || v.plateAr.includes(search) || (v.plateLatin ?? '').toLowerCase().includes(search.toLowerCase()) || (v.driver?.fullName ?? '').includes(search) || (v.name ?? '').includes(search))),
    [vehicles, hiddenGroups, statusFilter, search],
  );
  const markers = useMemo<VehicleMarker[]>(() => visible.filter((v) => v.live.lat !== null && v.live.lon !== null).map((v) => ({ id: v.id, label: v.plateAr, lat: v.live.lat!, lon: v.live.lon!, heading: v.live.heading, status: v.live.status, speedKph: v.live.speedKph, kind: vehicleKind(v.type) })), [visible]);
  const selected = vehicles.find((v) => v.id === selectedId) ?? null;

  // Trip of the selected vehicle: the running one (path driven so far + remaining path) or the next scheduled one.
  const tripsAllowed = can('trips:read');
  const active = useQuery({
    queryKey: ['trip-active', selectedId, selected?.live.tripId ?? null],
    enabled: !!selectedId && tripsAllowed,
    queryFn: () => api<VehicleActiveTrip>(`/trips/active?vehicleId=${selectedId}`),
    refetchInterval: 60_000,
  });
  const tripId = active.data?.tripId ?? null;
  const tripKind = active.data?.kind ?? null;
  const tripEnabled = !!tripId && tripsAllowed;
  const progress = useQuery({ queryKey: ['trip-progress', tripId], enabled: tripEnabled, queryFn: () => api<TripProgress>(`/trips/${tripId}/progress`), refetchInterval: 30_000 });
  useSocketEvent(
    'trip.updated',
    useCallback(
      (e: { tripId: string; vehicleId: string | null }) => {
        void qc.invalidateQueries({ queryKey: ['live-trips'] });
        if (e.tripId === tripId || (selectedId && e.vehicleId === selectedId)) {
          void qc.invalidateQueries({ queryKey: ['trip-active', selectedId] });
          void qc.invalidateQueries({ queryKey: ['trip-progress', e.tripId] });
        }
      },
      [tripId, selectedId, qc],
    ),
  );
  const prog = progress.data && progress.data.tripId === tripId ? progress.data : null;

  // Every running trip is drawn all the time, each in its own color; the driven path ends at the vehicle's live position.
  const liveTrips = useQuery({ queryKey: ['live-trips'], enabled: tripsAllowed, queryFn: () => api<LiveTrip[]>('/trips/live'), refetchInterval: 30_000 });
  const livePosById = useMemo(() => {
    const m = new Map<string, { lat: number; lon: number }>();
    for (const v of vehicles) if (v.live.lat !== null && v.live.lon !== null) m.set(v.id, { lat: v.live.lat, lon: v.live.lon });
    return m;
  }, [vehicles]);
  const liveTripPaths = useMemo<LiveTripPath[]>(
    () =>
      (liveTrips.data ?? []).map((tr) => {
        const pos = livePosById.get(tr.vehicleId);
        const driven = pos ? [...tr.driven, pos] : tr.driven;
        const remaining = pos && tr.remainingPath.length ? [pos, ...tr.remainingPath.slice(1)] : tr.remainingPath;
        return { tripId: tr.tripId, color: tripColor(tr.tripId), driven, remaining, selected: tr.tripId === tripId };
      }),
    [liveTrips.data, livePosById, tripId],
  );
  const waypointMarkers = useCallback(
    (waypoints: TripProgress['waypoints'], color: string, prefix: string): PointMarker[] =>
      waypoints.map((w) =>
        w.kind === 'origin'
          ? { id: `${prefix}-start`, lat: w.lat, lon: w.lon, label: w.label ?? t('trips.origin'), glyph: 'A', color }
          : w.kind === 'dest'
            ? { id: `${prefix}-end`, lat: w.lat, lon: w.lon, label: w.label ?? t('trips.dest'), glyph: 'B', color }
            : { id: `${prefix}-stop-${w.stopId}`, lat: w.lat, lon: w.lon, label: w.label ?? `${t('trips.stop')} ${w.seq}`, glyph: String(w.seq), color: w.done ? '#16A34A' : color },
      ),
    [t],
  );
  const liveTripMarkers = useMemo<PointMarker[]>(() => (liveTrips.data ?? []).flatMap((tr) => waypointMarkers(tr.waypoints, tripColor(tr.tripId), tr.tripId)), [liveTrips.data, waypointMarkers]);

  // The selected vehicle's *upcoming* trip (not running yet, so not in the live set): planned path dashed + its waypoints.
  const upcomingPoints = useMemo<RoutePoint[]>(() => (prog && tripKind === 'upcoming' ? prog.remainingPath.map((p) => ({ lat: p.lat, lon: p.lon })) : []), [prog, tripKind]);
  const upcomingMarkers = useMemo<PointMarker[]>(() => (prog && tripKind === 'upcoming' ? waypointMarkers(prog.waypoints, '#DB2777', 'upcoming') : []), [prog, tripKind, waypointMarkers]);

  // Fit the selected vehicle's whole trip (start → path → destination) once per trip, unless following the vehicle.
  const fittedTrip = useRef<string | null>(null);
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !tripId || follow || fittedTrip.current === tripId) return;
    const own = liveTripPaths.find((p) => p.tripId === tripId);
    const pts: { lat: number; lon: number }[] = own
      ? [...own.driven, ...own.remaining, ...liveTripMarkers.filter((mk) => mk.id.startsWith(tripId))]
      : [...upcomingPoints, ...upcomingMarkers];
    if (pts.length < 2) return;
    fittedTrip.current = tripId;
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    for (const p of pts) { minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat); minLon = Math.min(minLon, p.lon); maxLon = Math.max(maxLon, p.lon); }
    m.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: { top: 70, bottom: 260, left: 60, right: 60 }, duration: 700, maxZoom: 15 });
  }, [tripId, liveTripPaths, liveTripMarkers, upcomingPoints, upcomingMarkers, follow]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { MOVING: 0, STOPPED: 0, IDLE: 0, OFFLINE: 0, UNKNOWN: 0 };
    for (const v of vehicles) c[v.live.status] = (c[v.live.status] ?? 0) + 1;
    return c;
  }, [vehicles]);

  const grouped = useMemo(() => {
    const byGroup = new Map<string, LiveVehicle[]>();
    for (const v of visible) byGroup.set(v.groupId ?? '__none', [...(byGroup.get(v.groupId ?? '__none') ?? []), v]);
    const list = (groups.data ?? []).map((g) => ({ id: g.id, name: g.name, color: g.color, items: byGroup.get(g.id) ?? [], total: vehicles.filter((v) => v.groupId === g.id).length }));
    const none = byGroup.get('__none') ?? [];
    if (none.length || vehicles.some((v) => !v.groupId)) list.push({ id: '__none', name: t('live.ungrouped'), color: null, items: none, total: vehicles.filter((v) => !v.groupId).length });
    return list;
  }, [visible, vehicles, groups.data, t]);

  const select = useCallback((id: string | null) => {
    setSelectedId(id);
    setParams(id ? { vehicle: id } : {}, { replace: true });
    if (!id) setFollow(false);
  }, [setParams]);

  // fly to selected / follow
  useEffect(() => {
    if (!selected || selected.live.lat === null || selected.live.lon === null) return;
    const m = mapRef.current;
    if (!m) return;
    if (follow) m.easeTo({ center: [selected.live.lon, selected.live.lat], duration: 800 });
  }, [selected?.live.lat, selected?.live.lon, follow, selected]);
  const flyToSelected = (minZoom = 14) => {
    if (selected && selected.live.lat !== null && selected.live.lon !== null) mapRef.current?.flyTo({ center: [selected.live.lon, selected.live.lat], zoom: Math.max(mapRef.current.getZoom(), minZoom), duration: 600 });
  };
  useEffect(() => {
    fittedTrip.current = null;
    if (selectedId) flyToSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const toggleGroup = (id: string) => setHiddenGroups((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCollapse = (id: string) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="flex h-[calc(100dvh-140px)] min-h-[560px] flex-col gap-3 lg:flex-row">
      {/* ── list */}
      <aside className="card flex w-full flex-col overflow-hidden lg:w-[360px] lg:flex-none">
        <div className="flex flex-col gap-2 border-b border-border-soft p-3">
          <input className="input" placeholder={t('live.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
          <div className="flex flex-wrap gap-1">
            {(['', 'MOVING', 'STOPPED', 'IDLE', 'OFFLINE'] as const).map((s) => (
              <button key={s} className={clsx('pill border', statusFilter === s ? 'border-brand-ink bg-brand-soft text-brand-ink' : 'border-border text-text-muted')} onClick={() => setStatusFilter(s)}>
                {s ? <><span className={clsx('size-1.5 rounded-full', BAR[s])} />{t(`status.${s}`)} {counts[s] ?? 0}</> : <>{t('common.all')} {vehicles.length}</>}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {snapshot.isLoading ? <div className="p-3"><Skeleton className="h-64" /></div> : grouped.map((g) => (
            <section key={g.id}>
              <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border-soft bg-surface-2 px-3 py-2 text-xs font-semibold">
                <button className="flex flex-1 items-center gap-2 text-start" onClick={() => toggleCollapse(g.id)} aria-expanded={!collapsed.has(g.id)}>
                  <ChevronDown size={14} className={clsx('transition-transform', collapsed.has(g.id) && 'rtl:rotate-90 ltr:-rotate-90')} />
                  {g.color ? <span className="size-2 rounded-full" style={{ background: g.color }} /> : null}
                  <span className="flex-1 truncate">{g.name}</span>
                  <span className="text-text-muted">{g.items.length} / {g.total}</span>
                </button>
                <button className="btn-ghost size-7 !p-0" onClick={() => toggleGroup(g.id)} aria-label={hiddenGroups.has(g.id) ? t('live.showGroup') : t('live.hideGroup')} title={hiddenGroups.has(g.id) ? t('live.showGroup') : t('live.hideGroup')}>
                  {hiddenGroups.has(g.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {!collapsed.has(g.id) && !hiddenGroups.has(g.id) ? g.items.map((v) => (
                <button key={v.id} className={clsx('flex w-full items-stretch gap-2 border-b border-border-soft text-start hover:bg-surface-hover', selectedId === v.id && 'bg-brand-soft')} onClick={() => select(v.id)}>
                  <span className={clsx('w-1 flex-none', BAR[v.live.status])} aria-hidden />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5 py-2">
                    <span className="flex items-center gap-2"><span className="truncate font-bold">{v.plateAr}</span><span className="ms-auto text-xs font-semibold text-text-2">{v.live.speedKph !== null && v.live.status === 'MOVING' ? `${Math.round(v.live.speedKph)} ${t('units.kph')}` : ''}</span></span>
                    <span className="flex items-center gap-1.5 text-xs text-text-muted">
                      <span className={clsx('size-1.5 rounded-full', BAR[v.live.status])} />
                      {t(`status.${v.live.status}`)}{v.live.statusSince ? ` · ${fmtDuration((Date.now() - Date.parse(v.live.statusSince)) / 1000, i18n.language)}` : ''}
                    </span>
                    <span className="flex items-center gap-2 text-[11px] text-text-faint">
                      {v.live.ignition !== null ? <span className={clsx('flex items-center gap-0.5', v.live.ignition ? 'text-success-fg' : '')}><Key size={11} /> {v.live.ignition ? t('live.ignitionOn') : t('live.ignitionOff')}</span> : null}
                      {v.live.activeSource === 'OBD' ? <Radio size={11} /> : v.live.activeSource === 'PHONE' ? <Smartphone size={11} /> : null}
                      <span className="truncate">{v.driver?.fullName ?? ''}</span>
                    </span>
                  </span>
                </button>
              )) : null}
            </section>
          ))}
          {!snapshot.isLoading && !visible.length ? <p className="p-6 text-center text-sm text-text-muted">{t('common.empty')}</p> : null}
        </div>
      </aside>

      {/* ── map */}
      <div className="card relative min-h-[360px] flex-1 overflow-hidden">
        <MapView zoom={6} onReady={(m) => { mapRef.current = m; }}>
          <PlacesLayer places={places.data ?? []} />
          <VehiclesLayer vehicles={markers} selectedId={selectedId} onClick={select} zoomOnClick />
          <LiveTripsLayer trips={liveTripPaths} beforeId={VEHICLES_LAYER_ID} />
          <MarkersLayer id="live-trip-markers" markers={liveTripMarkers} beforeId={VEHICLES_LAYER_ID} />
          <RouteLayer id="trip-upcoming" points={upcomingPoints} fit={false} color="#DB2777" dashed beforeId={VEHICLES_LAYER_ID} />
          <MarkersLayer id="trip-upcoming-markers" markers={upcomingMarkers} beforeId={VEHICLES_LAYER_ID} />
        </MapView>
        <div className="absolute end-3 top-3 flex items-center gap-2 rounded-lg border border-border bg-surface/95 px-3 py-1.5 text-xs shadow-card">
          <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-success" />{t('status.MOVING')} {counts['MOVING']}</span>
          <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-warning" />{t('status.IDLE')} {counts['IDLE']}</span>
          <span className="flex items-center gap-1"><span className="size-2 rounded-full bg-neutral" />{t('status.STOPPED')} {counts['STOPPED']}</span>
          <span className="flex items-center gap-1 text-text-muted">{t('status.OFFLINE')} {counts['OFFLINE']}</span>
        </div>

        {selected ? (
          <div className="absolute inset-x-3 bottom-3 flex flex-col gap-2 rounded-xl border border-border bg-surface/95 p-3 shadow-overlay lg:inset-x-auto lg:end-3 lg:w-[360px]">
            <div className="flex items-start gap-3">
              <span className={clsx('mt-1.5 size-3 rounded-full', BAR[selected.live.status])} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="text-lg font-bold">{selected.plateAr}</span><Badge tone={TONE[selected.live.status]}>{t(`status.${selected.live.status}`)}</Badge></div>
                <div className="truncate text-xs text-text-muted">{selected.driver?.fullName ?? t('vehicles.noDriver')}{selected.live.placeName ? ` · ${selected.live.placeName}` : ''}</div>
              </div>
              <button className="btn-ghost size-8 !p-0" aria-label={t('common.cancel')} onClick={() => select(null)}><X size={16} /></button>
            </div>
            <dl className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-md bg-surface-2 p-2"><dt className="text-text-muted">{t('live.speed')}</dt><dd className="text-base font-bold">{selected.live.speedKph !== null ? Math.round(selected.live.speedKph) : '—'} <span className="text-[10px] font-normal">{t('units.kph')}</span></dd></div>
              <div className="rounded-md bg-surface-2 p-2"><dt className="text-text-muted">{t('vehicles.odometer')}</dt><dd className="text-base font-bold">{fmtNumber(selected.live.odometerKm, i18n.language)} <span className="text-[10px] font-normal">{t('units.km')}</span></dd></div>
              <div className="rounded-md bg-surface-2 p-2"><dt className="text-text-muted">{t('live.lastUpdate')}</dt><dd className="text-sm font-bold">{fmtAgo(selected.live.lastAt, i18n.language)}</dd></div>
            </dl>
            {selected.live.fuelLevelPct !== null ? <div className="text-xs text-text-muted">{t('live.fuel')}: <b className="text-text">{Math.round(selected.live.fuelLevelPct)}%</b></div> : null}
            {tripsAllowed ? (
              prog && tripKind ? (
                <TripProgressCard p={prog} kind={tripKind} />
              ) : active.isFetched && !tripId ? (
                <div className="flex items-center gap-2 rounded-md bg-surface-2 px-2 py-1.5 text-xs text-text-muted">
                  <Route size={14} className="flex-none" />
                  <span className="flex-1">{t('live.noTrip')}</span>
                  {can('trips:create') ? <Link to="/trips/new" className="flex items-center gap-1 font-semibold text-brand-ink hover:underline"><Plus size={12} /> {t('live.planTrip')}</Link> : null}
                </div>
              ) : tripId ? (
                <Skeleton className="h-24" />
              ) : null
            ) : null}
            <div className="flex flex-wrap gap-1.5">
              <button className="btn-secondary !min-h-8 !px-2.5 text-xs" onClick={() => flyToSelected(16)}><ZoomIn size={14} /> {t('live.zoom')}</button>
              <button className={clsx('btn-secondary !min-h-8 !px-2.5 text-xs', follow && '!border-brand-ink !bg-brand-soft')} onClick={() => { setFollow((f) => !f); flyToSelected(); }}><Crosshair size={14} /> {t('live.follow')}</button>
              <Link to={`/history?vehicle=${selected.id}`} className="btn-secondary !min-h-8 !px-2.5 text-xs"><History size={14} /> {t('live.history')}</Link>
              {selected.driver?.phone ? <a href={`tel:${selected.driver.phone}`} className="btn-secondary !min-h-8 !px-2.5 text-xs"><Phone size={14} /> {t('live.call')}</a> : null}
              <Link to={`/vehicles/${selected.id}`} className="btn-ghost !min-h-8 !px-2.5 text-xs">{t('vehicles.details')}</Link>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
